import axios from 'axios';
import PusherService from '@/app/services/PusherService';
import { router } from '@inertiajs/vue3';
import { useSystemStoreWithOut } from '@/app/stores/system';
import { goLogout } from '@/app/helpers/routerHelper';
import { axiosXSRFTokenInterceptor } from '@/utils/csrfToken';
import { isTeacher } from '../helpers/userHelper';

let cancelTokenSource = axios.CancelToken.source(); // axios 공통 cancelToken

const httpClient = axios.create({
    withCredentials: true,
});

httpClient.interceptors.request.use(
    async (config) => {
        // XSRF-TOKEN 확인
        await axiosXSRFTokenInterceptor(config);

        // Socket ID가 있는 경우 헤더에 추가
        const socketId = PusherService.getInstance().echo.socketId();
        if (socketId) {
            config.headers['X-Socket-ID'] = socketId;
        }

        // 개발 환경에서만 XDEBUG 파라미터 추가
        if (process.env.NODE_ENV == 'development' && config.url) {
            // URL에 파라미터가 있는지 확인
            const separator = config.url.includes('?') ? '&' : '?';
            // URL에 XDEBUG_SESSION_START 파라미터 추가
            config.url = `${config.url}${separator}XDEBUG_SESSION_START=PHPSTORM`;
        }

        config.cancelToken = cancelTokenSource.token; // api 호출시마다 cancelToken 설정
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

httpClient.interceptors.response.use(
    (response) => Promise.resolve(response),
    (error) => {
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        if (!error.response) {
            return Promise.reject(error);
        }

        const { config, status, data } = error.response;

        // 에러 처리 콜백 호출 // post시
        if (config?.errorCallback) {
            config.errorCallback(error);
        } else if (config.params?.errorCallback) {
            //get 요청 에러처리 콜백
            // ex)
            // await httpClient.get(url, {
            //     params: {
            //         errorCallback: (error) => {
            //             // 에러 발생 시 사용자 정의 동작
            //             alert('데이터를 가져오는 중 문제가 발생했습니다. 다시 시도해 주세요.');
            //         },
            //     },
            // });
            config.params.errorCallback(error);
        } else if (status === 401) {
            if (isTeacher()) {
                return (window.location.href = route('teacher.login'));
            }

            const systemStore = useSystemStoreWithOut();

            systemStore.setModalState({
                show: true,
                title: data?.message || '세션 만료',
                message: data?.message || '세션이 만료되었습니다. 다시 로그인해주세요.',
                confirmEvent: () => {
                    router.get(route('app.login'));
                },
            });

            goLogout();
            return Promise.reject(error);
        } else if (status >= 400 && status < 500 && config.method !== 'get') {
            // 에러 메시지 있으면 알림 후 새로고침
            if (data?.errors?.error) {
                alert(data.errors.error);
            }
            // 400번대 에러시 페이지 새로고침 (GET 요청 제외)
            window.location.reload();
        } else if (status >= 500) {
            // 500 에러 발생 시 에러 페이지로 이동
            router.visit(`/errors?status=${status}`);
            return Promise.reject(error);
        }

        return Promise.reject(error);
    },
);

export const cancelPendingRequests = () => {
    try {
        cancelTokenSource.cancel('요청 취소');
        cancelTokenSource = axios.CancelToken.source();
    } catch (error) {
        console.log(error);
    }
};

export default httpClient;
